/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import {IconButton} from '../../components/buttons/IconButton';
import {SystemSaveExportIcon} from '../../icons/SystemSaveExportIcon';
import ConditionalRender from '../../render/ConditionalRender';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {IconSize} from '../../types/types';
import Tooltip from '../tooltip/Tooltip';

const tooltip = makeStyles(theme => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'iconTooltip'),
    fontSize : '12px !important'
  }
}));

export const ExportTooltip = function(props) {
  return <Tooltip arrow {...props} />;
};

// defines the admin container props ...
export type CSVExportButtonProps = {
  onExport: (params?: DownloadTemplate) => void,
  entity?: string,
  isExporting?: boolean,
  size?: IconSize,
  enableExport?: boolean,
  exportLimit?: boolean,
  disableExport?: boolean,
  hasNoRowSelection?: boolean,
  devicePwdInfo?: boolean
};

/**
 * The CSVExportButton provides the Export Icon Button which provides the state
 * and labels common amongst CSV Exports.
 *
 * ```typescript
 *   import {CSVExportButton} from '../../components/buttons/';
 *
 *   <CSVExportButton isExporting={isExporting} onExport={onExport} />
 * ```
 */
export const CSVExportButton = ({
  onExport,
  entity,
  isExporting,
  hasNoRowSelection = false,
  enableExport = false,
  exportLimit = false,
  disableExport = false,
  devicePwdInfo = false,
  size = IconSize.medium
}: CSVExportButtonProps) => {
  const [toolTipTitle, setToolTipTitle] = useState<string>('');
  const commonExport                    = translate('common:export', {returnObjects: true, entity});

  useEffect(() => {
    if (hasNoRowSelection && enableExport) {
      return setToolTipTitle(commonExport.emptyDisableTooltip);
    } if (enableExport) {
      return setToolTipTitle(commonExport.disableTooltip);
    } if (exportLimit && disableExport) {
      return setToolTipTitle(commonExport.noCapTooltip);
    } if (!isExporting) {
      return setToolTipTitle(commonExport.tooltip);
    } else {
      return setToolTipTitle(commonExport.tooltipPending);
    }
  }, [
    isExporting,
    enableExport,
    exportLimit,
    disableExport,
    toolTipTitle,
    hasNoRowSelection
  ]);

  return (
    <>

      <ConditionalRender condition={devicePwdInfo}>
        <Tooltip title={<span>{translate('admin-devices:devicePassword.dialog.tooltipInfo')}</span>}
          placement={'bottom'}
          classes={tooltip()}
          arrow>
          <span style={{marginTop: '12px'}}>
            <InfoIcon />
          </span>
        </Tooltip>
      </ConditionalRender>
      <ExportTooltip title={toolTipTitle}>
        <div>
          <IconButton icon={<SystemSaveExportIcon size={size} />}
            disabled={isExporting || enableExport}
            isDisableBackgroundColor={false}
            onClick={onExport} />
        </div>
      </ExportTooltip>
    </>
  );
};

export default CSVExportButton;
